'use client';

import React, { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface ChecklistItem {
  id: string;
  item: string;
  category: string;
  description: string;
}

interface EmergencyKitChecklistProps {
  items: ChecklistItem[];
}

const EmergencyKitChecklist: React.FC<EmergencyKitChecklistProps> = ({ items }) => {
  const [checkedItems, setCheckedItems] = useState<string[]>([]);
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
    const saved = localStorage.getItem('emergencyKitChecklist');
    if (saved) {
      setCheckedItems(JSON.parse(saved));
    }
  }, []);

  const toggleItem = (itemId: string) => {
    if (!isHydrated) return;
    
    const updated = checkedItems.includes(itemId)
      ? checkedItems.filter(id => id !== itemId)
      : [...checkedItems, itemId];
    
    setCheckedItems(updated);
    localStorage.setItem('emergencyKitChecklist', JSON.stringify(updated));
  };

  const categories = Array.from(new Set(items.map(item => item.category)));
  const progress = isHydrated ? Math.round((checkedItems.length / items.length) * 100) : 0;

  if (!isHydrated) {
    return (
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-muted rounded w-3/4"></div>
          <div className="h-4 bg-muted rounded w-full"></div>
          <div className="h-4 bg-muted rounded w-5/6"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="mb-6">
        <h3 className="text-2xl font-headline font-bold text-foreground mb-2 flex items-center space-x-2">
          <Icon name="ShoppingBagIcon" size={28} variant="solid" className="text-primary" />
          <span>Emergency Kit Checklist</span>
        </h3>
        <p className="text-muted-foreground mb-4">
          Prepare your pet emergency kit with these essential items
        </p>
        
        <div className="bg-muted rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-semibold text-foreground">Completion Progress</span>
            <span className="text-sm font-bold text-primary">{progress}%</span>
          </div>
          <div className="w-full bg-background rounded-full h-3 overflow-hidden">
            <div
              className="h-full bg-primary transition-all duration-500"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      </div>

      <div className="space-y-6">
        {categories.map((category) => (
          <div key={category}>
            <h4 className="text-lg font-headline font-semibold text-foreground mb-3 flex items-center space-x-2">
              <Icon name="FolderIcon" size={20} variant="solid" className="text-primary" />
              <span>{category}</span>
            </h4>
            <div className="space-y-2">
              {items
                .filter(item => item.category === category)
                .map((item) => {
                  const isChecked = checkedItems.includes(item.id);
                  return (
                    <div
                      key={item.id}
                      onClick={() => toggleItem(item.id)}
                      className={`border rounded-lg p-4 cursor-pointer transition-all duration-300 ${
                        isChecked
                          ? 'bg-success/10 border-success' :'bg-background border-border hover:border-primary/50'
                      }`}
                    >
                      <div className="flex items-start space-x-3">
                        <div className={`flex-shrink-0 w-6 h-6 rounded border-2 flex items-center justify-center transition-all duration-300 ${
                          isChecked
                            ? 'bg-success border-success' :'border-muted-foreground'
                        }`}>
                          {isChecked && (
                            <Icon name="CheckIcon" size={16} variant="solid" className="text-white" />
                          )}
                        </div>
                        <div className="flex-1">
                          <h5 className={`font-semibold mb-1 ${
                            isChecked ? 'text-success line-through' : 'text-foreground'
                          }`}>
                            {item.item}
                          </h5>
                          <p className="text-sm text-muted-foreground">{item.description}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
        ))}
      </div>

      {progress === 100 && (
        <div className="mt-6 bg-success/10 border-2 border-success rounded-lg p-4 flex items-center space-x-3">
          <Icon name="CheckCircleIcon" size={32} variant="solid" className="text-success" />
          <div>
            <h5 className="font-headline font-bold text-success mb-1">Kit Complete!</h5>
            <p className="text-sm text-foreground">Your emergency kit is ready. Review and update it regularly.</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmergencyKitChecklist;